import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Menu, Home, Ticket, CalendarDays, CalendarClock, Users, BarChart3, Settings as SettingsIcon, Plus, LogOut, ChevronLeft, PhoneOff, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { key: "home", label: "Home", icon: Home },
  { key: "tours", label: "Tours", icon: Ticket },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "future", label: "Future Requests", icon: CalendarClock },
  { key: "guests", label: "Guests", icon: Users },
  { key: "insights", label: "Insights", icon: BarChart3 },
  { key: "callouts", label: "Time & Attendance", icon: PhoneOff },
  { key: "settings", label: "Settings", icon: SettingsIcon },
  { key: "admin", label: "Admin", icon: ShieldCheck, adminOnly: true },
];

// Right-side slide-out menu: global search, Add tour, all sections, and account footer.
export const HamburgerMenu = ({ activeTab, onNavigate, pendingCount = 0 }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [showHint, setShowHint] = useState(() => { try { return !localStorage.getItem("sd_swipe_hint_seen"); } catch { return false; } });
  const dismissHint = () => { setShowHint(false); try { localStorage.setItem("sd_swipe_hint_seen", "1"); } catch { /* ignore */ } };

  // Auto-hide the discovery hint, and clear it once the menu is opened by any means.
  useEffect(() => { if (open && showHint) dismissHint(); /* eslint-disable-next-line */ }, [open]);
  useEffect(() => { if (!showHint) return; const t = setTimeout(dismissHint, 7000); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [showHint]);

  // Swipe in from the right edge to open the menu one-handed.
  useEffect(() => {
    let start = null;
    const onStart = (e) => {
      const t = e.touches[0];
      start = t && t.clientX >= window.innerWidth - 28 ? { x: t.clientX, y: t.clientY } : null;
    };
    const onEnd = (e) => {
      if (!start) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - start.x, dy = t.clientY - start.y;
      start = null;
      if (dx < -55 && Math.abs(dx) > Math.abs(dy) * 1.2) setOpen(true);
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => { window.removeEventListener("touchstart", onStart); window.removeEventListener("touchend", onEnd); };
  }, []);

  const go = (key) => { setOpen(false); onNavigate?.(key); };
  const addTour = () => { setOpen(false); navigate("/guest/new"); };

  return (
    <>
      {showHint && typeof document !== "undefined" && createPortal(
        <button
          onClick={() => setOpen(true)}
          className="swipe-hint fixed right-0 top-1/2 z-40 flex items-center gap-1 rounded-l-full bg-[#9B2226] py-2 pl-3 pr-2 text-white shadow-xl ring-1 ring-black/20"
          aria-label="Open menu — swipe from the right edge"
          data-testid="swipe-hint"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="pr-0.5 text-[11px] font-600">Menu</span>
        </button>,
        document.body
      )}
      <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="hdr-chip relative flex h-9 w-9 items-center justify-center rounded-lg border transition-colors hover:text-white"
          aria-label="Menu"
          data-testid="hamburger-btn"
        >
          <Menu className="h-4 w-4" />
          {pendingCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-700 text-zinc-950 ring-2 ring-zinc-950" data-testid="hamburger-pending-badge">
              {pendingCount}
            </span>
          )}
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="flex w-[86%] max-w-sm flex-col gap-0 border-l border-zinc-800 bg-zinc-950 p-0" data-testid="hamburger-drawer">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <SheetDescription className="sr-only">App navigation, search, and account</SheetDescription>
        <div className="drawer-item border-b border-zinc-800/80 p-4">
          <div className="mb-3 flex items-center gap-2.5">
            <img src="/icon-512.png" alt="Stage Door" draggable={false} className="h-9 w-9 select-none rounded-xl ring-1 ring-white/10" />
            <div className="min-w-0">
              <p className="truncate font-display text-sm font-700 text-zinc-100">Stage Door</p>
              <p className="text-[11px] uppercase tracking-wider text-[#E0575B]">VIP All-In-One</p>
            </div>
          </div>
          <GlobalSearch variant="full" />
          <Button onClick={addTour} className="mt-3 w-full bg-[#9B2226] text-white hover:bg-[#7f1c1f]" data-testid="drawer-add-tour">
            <Plus className="mr-1.5 h-4 w-4" /> Add tour
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3">
          {NAV.filter((item) => !item.adminOnly || user?.role === "admin").map((item, i) => {
            const Icon = item.icon;
            const active = activeTab === item.key;
            const showBadge = item.key === "admin" && pendingCount > 0;
            return (
              <button
                key={item.key}
                onClick={() => go(item.key)}
                data-testid={`drawer-nav-${item.key}`}
                style={{ animationDelay: `${0.06 + i * 0.045}s` }}
                className={`drawer-item relative mb-1 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-500 transition-all duration-200 ${active ? "bg-[#9B2226]/20 text-[#E0575B]" : "text-zinc-300 hover:translate-x-0.5 hover:bg-zinc-800/70 hover:text-zinc-100"}`}
              >
                {active && <span className="drawer-accent absolute left-0 top-1/2 h-6 w-1 rounded-r-full bg-[#E0575B]" />}
                <Icon className={`h-5 w-5 ${active ? "text-[#E0575B]" : "text-zinc-400"}`} />
                <span className="flex-1">{item.label}</span>
                {showBadge && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-700 text-zinc-950" data-testid="drawer-admin-badge">
                    {pendingCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="drawer-item border-t border-zinc-800/80 p-4" style={{ animationDelay: `${0.06 + NAV.length * 0.045}s` }}>
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#9B2226]/20 text-sm font-700 text-[#E0575B]">
              {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-500 text-zinc-100" data-testid="drawer-user-name">{user?.name || "Signed in"}</p>
              {user?.email && <p className="truncate text-xs text-zinc-500">{user.email}</p>}
            </div>
          </div>
          <Button onClick={() => logout()} variant="outline" className="w-full border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-white" data-testid="drawer-signout">
            <LogOut className="mr-1.5 h-4 w-4" /> Sign out
          </Button>
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
};
